import { loadSewMimicModule, getSewMimicModule, type SewMimicInitOptions, type SewMimicModule } from './runtime';

export interface ArmPose {
	shoulder: ReadonlyArray<number> | Float32Array | Float64Array;
	elbow: ReadonlyArray<number> | Float32Array | Float64Array;
	wrist: ReadonlyArray<number> | Float32Array | Float64Array;
	wristRotation?: ReadonlyArray<number> | ReadonlyArray<ReadonlyArray<number>> | Float32Array | Float64Array;
}

export interface SolveIKRequest {
	left?: ArmPose;
	right?: ArmPose;
	torso?: number[];
	torsoRotation?: ReadonlyArray<number> | ReadonlyArray<ReadonlyArray<number>> | Float32Array | Float64Array;
}

export interface ArmSolution {
	q: number[];
	isLeastSquares: boolean;
}

export interface G1JointSolution {
	left_arm: number[];
	right_arm: number[];
	torso?: number[];
	control_commands?: number[];
	metadata?: {
		left?: ArmSolution;
		right?: ArmSolution;
		torsoRotation?: number[];
	};
}

export class SewMimicG1Controller {
	private module: SewMimicModule | null = null;

	async initialize(options: SewMimicInitOptions = {}): Promise<void> {
		this.module = await loadSewMimicModule(options);
	}

	isReady(): boolean {
		return this.module !== null || getSewMimicModule() !== null;
	}

	async solveIK(request: SolveIKRequest): Promise<G1JointSolution> {
		if (!request.left && !request.right) {
			throw new Error('At least one arm pose is required to solve IK');
		}

		const module = this.ensureModule();
		const result: G1JointSolution = { left_arm: [], right_arm: [] };
		const metadata: NonNullable<G1JointSolution['metadata']> = {};

		const spec: Record<string, Float64Array> = {}; // per-group qpos vectors

		if (request.left) {
			const left = this.solveArm(module, 'left', request.left);
			result.left_arm = left.q;
			metadata.left = left;
			spec.left_arm = new Float64Array(left.q);
		}

		if (request.right) {
			const right = this.solveArm(module, 'right', request.right);
			result.right_arm = right.q;
			metadata.right = right;
			spec.right_arm = new Float64Array(right.q);
		}

		if (request.torsoRotation) {
			const r_torso = toFloat64Rotation(request.torsoRotation);
			// console.log('r_torso', r_torso);
			const q_torso = module.g1_torso_ik(r_torso);
			result.torso = Array.from(q_torso);
			spec.torso = q_torso;
			metadata.torsoRotation = Array.from(r_torso);
		} else if (request.torso && request.torso.length === 3) {
			result.torso = request.torso;
			spec.torso = new Float64Array(request.torso);
		}

		if (Object.keys(metadata).length > 0) {
			result.metadata = metadata;
		}

		// Compose actuator-indexed control vector from group joints.
		// Assumes g1_to_actuator_qpos is always available in the wasm module.
		const qpos = (module as unknown as { g1_to_actuator_qpos: (s: Record<string, Float64Array>) => Float64Array }).g1_to_actuator_qpos(spec);
		result.control_commands = Array.from(qpos);

		return result;
	}

	destroy(): void {
		this.module = null;
	}

	private ensureModule(): SewMimicModule {
		if (!this.module) {
			const cached = getSewMimicModule();
			if (!cached) {
				throw new Error('SEW-Mimic runtime not initialized. Call initialize() first.');
			}
			this.module = cached;
		}
		return this.module;
	}

	private solveArm(module: SewMimicModule, side: 'left' | 'right', pose: ArmPose): ArmSolution {
		const shoulder = toFloat64Vector(pose.shoulder);
		const elbow = toFloat64Vector(pose.elbow);
		const wrist = toFloat64Vector(pose.wrist);
		const wristRotation = pose.wristRotation ? toFloat64Rotation(pose.wristRotation) : null;

		const solution = module.g1_arm_from_points(side, shoulder, elbow, wrist, wristRotation);

		return {
			q: Array.from(solution.q),
			isLeastSquares: solution.isLeastSquares
		};
	}
}

function toFloat64Vector(value: ArmPose['shoulder']): Float64Array {
	const array = normalizeVector(value);
	if (!array) {
		throw new Error('Invalid vector provided to SEW-Mimic solver');
	}
	return new Float64Array(array);
}

function normalizeVector(value: unknown): number[] | null {
	if (!value) return null;

	if (value instanceof Float32Array || value instanceof Float64Array) {
		return Array.from(value.slice(0, 3));
	}

	if (Array.isArray(value)) {
		return value.slice(0, 3).map(Number);
	}

	if (typeof value === 'object') {
		const record = value as Record<string, unknown>;
		const xyz = ['x', 'y', 'z'];
		if (xyz.every((key) => typeof record[key] === 'number')) {
			return xyz.map((key) => Number(record[key]));
		}
		const numericValues = Object.values(record).filter((entry): entry is number => typeof entry === 'number');
		if (numericValues.length >= 3) {
			return numericValues.slice(0, 3).map(Number);
		}
	}

	return null;
}

function toFloat64Rotation(value: ArmPose['wristRotation']): Float64Array {
	if (!value) {
		return new Float64Array(9);
	}

	if (value instanceof Float32Array || value instanceof Float64Array) {
		const array = Array.from(value);
		return new Float64Array(array.slice(0, 9));
	}

	else if (Array.isArray(value)) {
		if (value.length === 9 && value.every((entry) => typeof entry === 'number')) {
			return new Float64Array(value as number[]);
		}

		if (value.length === 3 && value.every((row) => Array.isArray(row))) {
			const flattened = (value as ReadonlyArray<ReadonlyArray<number>>)
				.flat()
				.slice(0, 9)
				.map(Number);
			return new Float64Array(flattened);
		}
	}

	else if (typeof value === 'object') {
		const record = value as unknown as Record<string, unknown>;
		const numericValues = Object.values(record).filter((entry): entry is number => typeof entry === 'number');
		if (numericValues.length >= 9) {
			return new Float64Array(numericValues.slice(0, 9).map(Number));
		}
	}

	throw new Error('Invalid wrist rotation supplied to SEW-Mimic solver');
}
